import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'Hooks/useAuth';
import { useNotification } from 'Hooks/useNotification';
import { useFormValidation } from 'Hooks/useFormValidation';
import { ROUTES } from 'Utils/routes';
import { SignInFormErrors } from 'Types/forms';
import Copyright from 'Components/Copyright/Copyright';
import FormTextField from 'Components/FormTextField/FormTextField';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Divider from '@mui/material/Divider';
import GoogleIcon from 'Components/Icons/GoogleIcon';
import Typography from '@mui/material/Typography';
import signInPageBackground from 'Assets/Images/signInPageBackground.jpg';

export default function SignIn() {
  const auth = useAuth();
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const {
    values,
    errors,
    setFieldValue,
    clearFieldError,
    validateEmail,
    validateRequired,
    validateAll,
  } = useFormValidation<SignInFormErrors>({ email: '', password: '' });
  const [rememberMe, setRememberMe] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  // Clear password when navigating away for security
  useEffect(() => {
    return () => {
      setFieldValue('password', '');
    };
  }, [setFieldValue]);

  const validateForm = (): boolean => {
    return validateAll([
      () => validateEmail('email'),
      () => validateRequired('password', 'Password is required'),
    ]);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    const result = await auth.signIn(values.email, values.password, rememberMe);
    setIsLoading(false);

    if (result.success) {
      navigate(ROUTES.DASHBOARD);
    } else {
      showNotification(result.message);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    const result = await auth.signInWithGoogle();
    // Note: signInWithGoogle redirects to Google, so we only handle errors here
    if (!result.success) {
      setIsGoogleLoading(false);
      showNotification(result.message);
    }
  };

  return (
    <Grid container component='main' sx={{ height: '100vh' }}>
      <CssBaseline />
      <Grid
        item
        xs={false}
        sm={4}
        md={7}
        sx={{
          backgroundImage: `url(${signInPageBackground})`,
          backgroundRepeat: 'no-repeat',
          backgroundColor: (t) =>
            t.palette.mode === 'light' ? t.palette.grey[50] : t.palette.grey[900],
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
        <Box
          sx={{
            my: 8,
            mx: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'secondary.dark' }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component='h1' variant='h5'>
            Sign in
          </Typography>
          <Box component='form' noValidate onSubmit={handleSubmit} sx={{ mt: 1 }}>
            {auth.oauthError && (
              <Alert
                severity={auth.isLinkingSuccess ? 'success' : 'warning'}
                onClose={auth.clearOAuthError}
                sx={{ mb: 2 }}
              >
                {auth.oauthError}
              </Alert>
            )}
            <FormTextField
              margin='normal'
              required
              name='email'
              label='Email Address'
              autoComplete='email'
              autoFocus
              value={values.email}
              fieldError={errors.email}
              disabled={isLoading}
              onFieldChange={setFieldValue}
              onClearError={clearFieldError}
            />
            <FormTextField
              margin='normal'
              required
              name='password'
              label='Password'
              type='password'
              autoComplete='current-password'
              value={values.password}
              fieldError={errors.password}
              disabled={isLoading}
              onFieldChange={setFieldValue}
              onClearError={clearFieldError}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  color='info'
                  disabled={isLoading}
                />
              }
              label='Remember me'
            />
            <Button
              type='submit'
              fullWidth
              variant='contained'
              color='secondary'
              disabled={isLoading || isGoogleLoading}
              sx={{ mt: 3, mb: 2 }}
            >
              {isLoading ? <CircularProgress size={24} color='inherit' /> : 'Sign in'}
            </Button>
            {auth.isOAuthEnabled && (
              <>
                <Divider sx={{ my: 2 }}>or</Divider>
                <Button
                  fullWidth
                  variant='outlined'
                  startIcon={<GoogleIcon />}
                  disabled={isLoading || isGoogleLoading}
                  onClick={handleGoogleSignIn}
                  sx={{
                    mb: 2,
                    borderColor: 'grey.400',
                    color: 'text.primary',
                    '&:hover': {
                      borderColor: 'grey.600',
                      bgcolor: 'grey.50',
                    },
                  }}
                >
                  {isGoogleLoading ? (
                    <CircularProgress size={24} color='inherit' />
                  ) : (
                    'Continue with Google'
                  )}
                </Button>
              </>
            )}
            <Grid container>
              <Grid item xs>
                <Link href={ROUTES.FORGOT_PASSWORD} variant='body2' color='secondary.contrastText'>
                  Forgot password?
                </Link>
              </Grid>
              <Grid item>
                <Link href={ROUTES.SIGN_UP} variant='body2' color='secondary.contrastText'>
                  {`Don't have an account? Sign Up`}
                </Link>
              </Grid>
            </Grid>
            <Copyright sx={{ mt: 5 }} />
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
}
